import { useState, type ComponentType } from 'react';

import { createFileRoute, Link, Outlet } from '@tanstack/react-router';

import { cn, Toaster, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@eureca/ui';
import { EurecaLogoMark } from '@eureca/ui/icons';

import { getKeycloak } from '@/auth/keycloak-instance';
import { canManageCatalog, isAdmin } from '@/auth/roles';

// ---------------------------------------------------------------------------
// Icon factory
// ---------------------------------------------------------------------------

function icon(paths: React.ReactNode) {
  return function Icon({ className }: { className?: string }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
      >
        {paths}
      </svg>
    );
  };
}

const HouseIcon = icon(
  <>
    <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
    <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </>
);

const UsersIcon = icon(
  <>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </>
);

const LogOutIcon = icon(
  <>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" x2="9" y1="12" y2="12" />
  </>
);

const DlqIcon = icon(
  <>
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </>
);

const CatalogIcon = icon(
  <>
    <path d="M22 10 12 5 2 10l10 5 10-5Z" />
    <path d="M6 12v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5" />
  </>
);

const AuditIcon = icon(
  <>
    <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="M12 11h4" />
    <path d="M12 16h4" />
    <path d="M8 11h.01" />
    <path d="M8 16h.01" />
  </>
);

const MoonIcon = icon(<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />);
const SunIcon = icon(
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="m4.93 4.93 1.41 1.41" />
    <path d="m17.66 17.66 1.41 1.41" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
    <path d="m6.34 17.66-1.41 1.41" />
    <path d="m19.07 4.93-1.41 1.41" />
  </>
);

// ---------------------------------------------------------------------------
// Nav config
// ---------------------------------------------------------------------------

type IconComponent = ComponentType<{ className?: string }>;

interface NavItem {
  label: string;
  Icon: IconComponent;
  to: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Início', Icon: HouseIcon, to: '/inicio' },
  { label: 'Usuários', Icon: UsersIcon, to: '/usuarios' },
];

/**
 * EU-732/EU-749 — catálogo de educação é dado-mestre compartilhado, mas é
 * operação de dia a dia: aparece para `admin` E para `customer-support`. A
 * recusa definitiva continua sendo o 403 do Core.
 */
const CATALOG_NAV_ITEMS: NavItem[] = [
  { label: 'Instituições & cursos', Icon: CatalogIcon, to: '/instituicoes-cursos' },
];

/**
 * Itens de admin — anexados a NAV_ITEMS quando o operador é admin.
 *
 * EU-749 — o DLQ entrou aqui. Ele sempre foi `admin-only` no Core, mas vivia no
 * menu de TODO operador, o que não incomodava enquanto todo operador tinha
 * `admin`. Com a separação de papéis o atendimento passava a abrir uma tela que
 * o Core recusa com 403 e que renderiza "0 pendentes" — uma DLQ falsamente
 * vazia, pior que um erro.
 */
const ADMIN_NAV_ITEMS: NavItem[] = [
  { label: 'DLQ', Icon: DlqIcon, to: '/dlq' },
  { label: 'Auditoria', Icon: AuditIcon, to: '/auditoria' },
];

// ---------------------------------------------------------------------------
// Shared icon-button style (compact sidebar — all controls are square)
// ---------------------------------------------------------------------------

const ICON_BTN = cn(
  'flex size-10 items-center justify-center rounded-md transition-colors',
  'text-foreground hover:bg-accent/50',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
);

// ---------------------------------------------------------------------------
// ThemeToggleButton
// ---------------------------------------------------------------------------

const THEME_KEY = 'backoffice-theme';

function ThemeToggleButton() {
  const [isDark, setIsDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );

  function toggle() {
    const next = document.documentElement.classList.toggle('dark');
    setIsDark(next);
    try {
      localStorage.setItem(THEME_KEY, next ? 'dark' : 'light');
    } catch {
      // localStorage indisponível (ex.: modo privado) — tema só não persiste.
    }
  }

  const Icon = isDark ? SunIcon : MoonIcon;
  const label = isDark ? 'Mudar para modo claro' : 'Mudar para modo escuro';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" aria-label={label} onClick={toggle} className={ICON_BTN}>
          <Icon className="size-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

function Sidebar() {
  const keycloak = getKeycloak();
  const navItems = [
    ...NAV_ITEMS,
    ...(canManageCatalog() ? CATALOG_NAV_ITEMS : []),
    ...(isAdmin() ? ADMIN_NAV_ITEMS : []),
  ];

  return (
    <aside
      aria-label="Menu do backoffice"
      className={cn(
        'sticky top-0 hidden h-dvh w-16 shrink-0 flex-col items-center justify-between',
        'border-r border-border bg-card py-6 md:flex'
      )}
    >
      {/* Top section */}
      <div className="flex w-full flex-col items-center gap-8">
        {/* Logo */}
        <div className="flex h-10 items-center justify-center">
          <EurecaLogoMark aria-label="Eureca" className="h-4 w-auto text-primary" />
        </div>

        {/* Nav */}
        <TooltipProvider delayDuration={0}>
          <nav aria-label="Navegação principal" className="w-full">
            <ul className="flex flex-col items-center gap-2">
              {navItems.map(({ label, Icon, to }) => (
                <li key={to}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to={to}
                        aria-label={label}
                        className={cn(
                          ICON_BTN,
                          '[&.active]:bg-secondary [&.active]:text-secondary-foreground'
                        )}
                      >
                        <Icon className="size-4" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right">{label}</TooltipContent>
                  </Tooltip>
                </li>
              ))}
            </ul>
          </nav>
        </TooltipProvider>
      </div>

      {/* Bottom section */}
      <TooltipProvider delayDuration={0}>
        <nav aria-label="Ações da conta" className="w-full">
          <ul className="flex flex-col items-center gap-2">
            <li>
              <ThemeToggleButton />
            </li>
            <li>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Sair"
                    onClick={() => void keycloak.logout({ redirectUri: window.location.origin })}
                    className={ICON_BTN}
                  >
                    <LogOutIcon className="size-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Sair</TooltipContent>
              </Tooltip>
            </li>
          </ul>
        </nav>
      </TooltipProvider>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Shell layout
// ---------------------------------------------------------------------------

function ShellLayout() {
  return (
    <div className="flex h-dvh flex-row overflow-hidden bg-card">
      <Sidebar />
      <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
        <Outlet />
      </main>
      <Toaster />
    </div>
  );
}

export const Route = createFileRoute('/_auth/_shell')({
  component: ShellLayout,
});
